import { useCallback, useState } from 'react';

import {
    IAttachment,
    IAttachmentUpload,
    ServerAttachment
} from '../types/attachments';
import { generateUniqueId } from './utils';

export interface UploadProgressCallback {
    (fileId: number, progress: number): void;
}

export interface UploadCompleteCallback {
    (fileId: number, success: boolean, error?: string): void;
}

export interface UploadOptions {
    uploadUrl: string;
    file: File;
    fileId: number;
    onProgress?: UploadProgressCallback;
    onComplete?: UploadCompleteCallback;
    signal?: AbortSignal;
}

export interface UploadResult {
    success: boolean;
    error?: string;
}

export const uploadFileWithProgress = async (
    options: UploadOptions
): Promise<UploadResult> => {
    const { uploadUrl, file, fileId, onProgress, onComplete, signal } = options;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);

        // Progress tracking
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                onProgress?.(fileId, progress);
            }
        };

        // Success handling
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                const result: UploadResult = { success: true };
                onComplete?.(fileId, true);
                resolve(result);
            } else {
                const error = `HTTP Error ${xhr.status}`;
                const result: UploadResult = { success: false, error };
                onComplete?.(fileId, false, error);
                reject(result);
            }
        };

        // Error handling
        xhr.onerror = () => {
            const error = 'Network Error';
            const result: UploadResult = { success: false, error };
            onComplete?.(fileId, false, error);
            reject(result);
        };

        // Abort handling
        xhr.onabort = () => {
            const error = 'Upload Cancelled';
            const result: UploadResult = { success: false, error };
            onComplete?.(fileId, false, error);
            reject(result);
        };

        // Listen for abort signal
        if (signal) {
            signal.addEventListener('abort', () => {
                xhr.abort();
            });
        }

        xhr.send(file);
    });
};

// React Hook for file uploads
export interface UseFileUploadOptions {
    getUploadUrl: (
        fileName: string,
        fileType: string,
        workId?: number
    ) => Promise<{ url: string; fileId: number }>;
    scanFile: (fileId: number) => Promise<{ isSafe: boolean }>;
    onComplete?: (fileIds: number[]) => void;
}

export interface UseFileUploadReturn {
    attachedFiles: IAttachmentUpload[];
    uploadFiles: (files: File[]) => Promise<number[]>;
    removeFile: (fileId: number) => void;
    cancelUpload: (fileId: number) => void;
    clearFiles: () => void;
    isUploading: boolean;
}

export const useFileUpload = (
    options: UseFileUploadOptions
): UseFileUploadReturn => {
    const { getUploadUrl, scanFile, onComplete } = options;
    const [ attachedFiles, setAttachedFiles ] = useState<IAttachmentUpload[]>([]);

    const updateFile = useCallback(
        (fileId: number, updates: Partial<IAttachmentUpload>) => {
            setAttachedFiles((prevFiles) =>
                prevFiles.map((f) =>
                    f.id === fileId ? { ...f, ...updates } : f
                )
            );
        },
        []
    );

    const removeFile = useCallback((fileId: number) => {
        setAttachedFiles((prevFiles) =>
            prevFiles.filter((f) => f.id !== fileId)
        );
    }, []);

    const cancelUpload = useCallback(
        (fileId: number) => {
            const fileToCancel = attachedFiles.find((f) => f.id === fileId);
            if (fileToCancel?.uploadController) {
                fileToCancel.uploadController.abort();
            }
            removeFile(fileId);
        },
        [ attachedFiles, removeFile ]
    );

    const clearFiles = useCallback(() => {
        setAttachedFiles([]);
    }, []);

    const uploadFiles = useCallback(
        async (files: File[]): Promise<number[]> => {
            const newAttachedFiles = createAttachmentUploads(files);

            setAttachedFiles((prevFiles) => [
                ...prevFiles,
                ...newAttachedFiles
            ]);

            try {
                const successfulFileIds = await processFiles({
                    files: newAttachedFiles,
                    getUploadUrl,
                    scanFile,
                    onFileUpdate: updateFile,
                    onFileRemove: removeFile,
                    onComplete
                });

                return successfulFileIds;
            } catch (error) {
                console.error('File upload error:', error);
                return [];
            }
        },
        [ getUploadUrl, scanFile, updateFile, removeFile, onComplete ]
    );

    const isUploading = attachedFiles.some(
        (file) => file.status === 'uploading'
    );

    return {
        attachedFiles,
        uploadFiles,
        removeFile,
        cancelUpload,
        clearFiles,
        isUploading
    };
};

export interface ProcessFilesOptions {
    files: IAttachmentUpload[];
    getUploadUrl: (
        fileName: string,
        fileType: string,
        workId?: number
    ) => Promise<{ url: string; fileId: number }>;
    scanFile: (fileId: number) => Promise<{ isSafe: boolean }>;
    onFileUpdate: (fileId: number, updates: Partial<IAttachmentUpload>) => void;
    onFileRemove: (fileId: number) => void;
    onComplete?: (fileIds: number[]) => void;
}

export const processFiles = async (
    options: ProcessFilesOptions
): Promise<number[]> => {
    const {
        files,
        getUploadUrl,
        scanFile,
        onFileUpdate,
        onFileRemove,
        onComplete
    } = options;
    const successfulFileIds: number[] = [];

    for (const file of files) {
        try {
            const response = await getUploadUrl(file.file.name, file.file.type, file.workId);
            const { url: uploadUrl, fileId } = response;

            // Update file with backend fileId
            onFileUpdate(file.id, { fileId });

            // Create abort controller
            const abortController = new AbortController();
            onFileUpdate(file.id, { uploadController: abortController });

            // Upload file with progress
            await uploadFileWithProgress({
                uploadUrl,
                file: file.file,
                fileId: file.id,
                signal: abortController.signal,
                onProgress: (uploadFileId, progress) => {
                    onFileUpdate(uploadFileId, { uploadProgress: progress });
                },
                onComplete: (uploadFileId, success) => {
                    if (success) {
                        onFileUpdate(uploadFileId, { status: 'completed' });
                    } else {
                        onFileUpdate(uploadFileId, { status: 'error' });
                    }
                }
            });

            // Scan file for safety
            const scanResult = await scanFile(fileId);

            if (!scanResult.isSafe) {
                onFileRemove(file.id);
                continue;
            }

            successfulFileIds.push(fileId);
        } catch (error) {
            console.error('Upload error:', error);
            onFileUpdate(file.id, { status: 'error' });
        }
    }

    onComplete?.(successfulFileIds);
    return successfulFileIds;
};

export const serverAttachmentToIAttachment = (
    serverAttachment: ServerAttachment
): IAttachment => {
    return {
        id: generateUniqueId(),
        fileId: serverAttachment.id,
        name: serverAttachment.name,
        type: serverAttachment.type
    };
};

export const mapServerAttachmentsToIAttachments = (
    serverAttachments: ServerAttachment[]
): IAttachment[] => {
    return serverAttachments.map(serverAttachmentToIAttachment);
};

export const createAttachmentUploads = (files: File[]): IAttachmentUpload[] => {
    return files.map((file) => ({
        id: generateUniqueId(),
        file,
        name: file.name,
        type: file.type,
        uploadProgress: 0,
        status: 'uploading' as const
    }));
};
